import axios from "axios";

export const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Short-lived access tokens are refreshed transparently: a single 401 on any
// call triggers one /auth/refresh attempt and replays the original request.
// If the refresh itself fails (session revoked or expired) the app drops to the
// sign-in gate.
let refreshing = null;

// Double-submit CSRF: the token is echoed back in a header on every
// state-changing request. It is kept in memory because some browsers (privacy
// modes, embedded/partitioned contexts) send cookies but block document.cookie
// reads, which previously left every POST rejected as "Request blocked".
let csrfToken = null;

export function setCsrfToken(token) {
  if (token) csrfToken = token;
}

export function clearCsrfToken() {
  csrfToken = null;
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Any response may carry a freshly rotated token (login, refresh, me, verify).
API.interceptors.response.use((res) => {
  setCsrfToken(res.data?.csrf_token);
  return res;
});

API.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    let token = csrfToken || readCookie("csrf_token");
    // After a page reload the in-memory token is gone, and some browsers never
    // store the readable cookie — ask the server for one rather than sending a
    // request that would be rejected as "Request blocked".
    if (!token && !config.url?.includes("/auth/me") && !config.url?.includes("/auth/login")) {
      try {
        const me = await API.get("/auth/me");
        token = me.data?.csrf_token || null;
      } catch {
        token = null;
      }
    }
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config || {};
    const url = original.url || "";
    // Password-confirmation endpoints answer 401 for "wrong password", which is
    // not an expired session — those must never tear the session down.
    const isAuthCall =
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/me") ||
      url.includes("/auth/mfa/") ||
      url.includes("/auth/change-password");

    if (err.response?.status === 401 && !isAuthCall && !original.__retried) {
      original.__retried = true;
      try {
        refreshing = refreshing || API.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return API(original);
      } catch {
        refreshing = null;
        window.dispatchEvent(new CustomEvent("virtue:session-expired"));
        return Promise.reject(err);
      }
    }

    if (err.response?.status === 401 && !isAuthCall) {
      window.dispatchEvent(new CustomEvent("virtue:session-expired"));
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
